// @todo move to new file depending on Network
import type { Abi, TransactionRequest } from "viem";

import {
  type AnyCell,
  type Cellified,
  type MapCell,
  SheetProxy,
  type ValueCell,
  cellify
} from "@okcontract/cells";
import {
  type AnonContractQueryType,
  type CacheQuery,
  type DataCacheType,
  type NFTQueryType,
  type OKWidget,
  type OKWidgetStepType,
  type TokenQueryType,
  WidgetQuery
} from "@okcontract/coredata";
import type { EditorMode, MapTypeDefinitions } from "@okcontract/fred";
import {
  Environment,
  Rational,
  type StandardLibrary,
  defaultLibrary,
  newTypeScheme,
  newTypeVar
} from "@okcontract/lambdascript";
import type { EnvironmentOptions } from "@okcontract/lambdascript/src/env"; // @todo export
import type { Value } from "@okcontract/lambdascript/src/eval";
import type {
  Address,
  ChainAddress,
  ChainType,
  EVMType,
  Network
} from "@okcontract/multichain";
import {
  type LocalRPCSubscriber,
  type LogEntry,
  estimateGas,
  nativeBalance
} from "@okcontract/multichain";
import type { LocalSubscriber } from "@scv/cache";
import type { OKData } from "@scv/cache/queryCache"; // @todo move

import { getABI } from "./abi";
import { retrieveAddress, retrieveAllAddresses } from "./address";
import { type AnalyzedLog, analyzeLog, analyzeLogs } from "./analyze";
import { callMethod } from "./contract";
import type { OKCore } from "./coreExecution";
import {
  formatAmount,
  formatBalance,
  getAllowanceForWallet,
  getBalance,
  getDecimals,
  getName,
  getSymbol,
  raw_balance
} from "./erc20";
import { owner_of, tokenURI_JSON, token_image } from "./erc721";
import { EnvKeyOKPage } from "./keys";
import { AddressExtension, web3Library } from "./lambdascript";
import { logSearch, logTitle } from "./log";
import { findName } from "./name";
import type { OKContractManager } from "./okcontract";
import { proxy_fetch, proxy_head } from "./proxy";
import { coredataTypeScheme } from "./schema";
import { Stepper } from "./stepper";
import { type Step, generateTX } from "./steps";
import type {
  AnyAddress,
  ContractQueryType,
  TokenQueryOrAddress
} from "./types";
import { newWidget, newWidgetFrom } from "./widget";

export type OKPageOptions = {
  local?: boolean;
  rpc?: boolean;
  name?: string;
  stdlib?: boolean;
  wantedChain?: ValueCell<ChainType>;
  onDestroy?: (fn: () => void) => void;
};

export class OKPage {
  readonly core: OKCore;
  readonly proxy: SheetProxy;
  readonly local: LocalSubscriber<CacheQuery>;
  readonly rpc: LocalRPCSubscriber;

  readonly null: MapCell<null, true>;
  readonly zero: MapCell<Rational, true>;
  readonly wantedChain: ValueCell<ChainType>;

  private _stdlib: StandardLibrary;
  private readonly _options: OKPageOptions;

  constructor(
    core: OKCore,
    options: OKPageOptions = { local: true, rpc: true }
  ) {
    // console.warn("create page", name);
    this.core = core;
    this._options = options;
    this.proxy = new SheetProxy(core.Sheet, options?.name || "OKP");
    this.null = this.proxy.new(null, "null") as unknown as MapCell<null, true>;
    this.zero = this.proxy.new(new Rational(0), "zero") as unknown as MapCell<
      Rational,
      true
    >;
    this.wantedChain =
      options?.wantedChain ||
      this.proxy.new(core.CurrentChain, `i:${options?.name}.wc`);
    // logger(this.wantedChain);
    if (options.local) {
      // this._proxy_local = new SheetProxy(core.Sheet, `cache:${options.name}`);
      this.local = core.Local(undefined, this.proxy, options?.name);
      // @todo maybe we should not reuse the proxy?
      // this._proxy = this._local._proxy;
    }
    if (options.rpc) {
      // this._proxy_rpc = new SheetProxy(core.Sheet, `rpc:${options.name}`);
      this.rpc = core.LocalRPC(undefined, this.proxy, options?.name);
    }
    if (options.onDestroy)
      options.onDestroy(() => {
        // console.warn("destroy page", name);
        this.destroy();
      });
  }

  destroy() {
    if (this?.local) this.local.destroy();
    // if (this._proxy_local) this._proxy_local.destroy();
    if (this?.rpc) this.rpc.destroy();
    // if (this._proxy_rpc) this._proxy_rpc.destroy();
    this.proxy.destroy();
  }

  _newWeb3Library = (): StandardLibrary => ({
    ...defaultLibrary(this.proxy),
    ...web3Library(this.proxy)
  });

  /**
   * envWithWeb3Library adds the complete web3 standard library to an Environment.
   * @param instance
   * @param env
   * @returns
   */
  envWithWeb3Library = (env: Environment) => {
    // @todo we could remove this trick with the ability to access "self" in
    // Standard Library definitions
    // If there is no standard library, we create it.
    // @todo Is the option to create at start useless?
    if (!this._stdlib) this._stdlib = this._newWeb3Library();
    env._updateLib(this._stdlib);
    if (env?.options?.noInstance) return env;
    env.addValueType(
      EnvKeyOKPage,
      // We cheat, this is an Instance class, not a Value.
      this as unknown as Value<unknown>,
      newTypeScheme(newTypeVar("i"), ["i"])
    );
    // chainable
    return env;
  };

  newEnvironment = (options?: EnvironmentOptions) => {
    return this.envWithWeb3Library(
      new Environment(this.proxy, {
        parseOptions: {
          // @todo check that we need both extension
          ext: [AddressExtension, { ...AddressExtension, elt: "NumberValue" }]
        },
        extensions: [AddressExtension],
        ...options
      })
    );
  };

  _dataCache<Q extends CacheQuery, V = OKData<Q> | null>(
    q: AnyCell<Q>,
    opts: {
      name?: string;
      returnErrors?: boolean;
    }
  ): AnyCell<V | null> {
    return this.local.unwrappedCell(q, opts?.name, opts?.returnErrors);
  }

  formatAmount(
    v: AnyCell<number | string | bigint | Rational>,
    tok: AnyCell<TokenQueryOrAddress>,
    chain: AnyCell<ChainType>,
    showSymbol: AnyCell<boolean> = this.proxy.new(true)
  ) {
    return formatAmount(
      this.core,
      this.proxy,
      this.rpc,
      this.local,
      v,
      tok,
      chain,
      showSymbol
    );
  }

  _formatBalance(
    walletID: AnyCell<Address<Network>>,
    ch: AnyCell<ChainType>,
    tok: AnyCell<TokenQueryOrAddress>
  ) {
    return formatBalance(
      this.core,
      this.proxy,
      this.rpc,
      this.local,
      walletID,
      ch,
      tok
    );
  }

  _getABI(addr: AnyCell<ChainAddress>, abi?: Abi) {
    return getABI(this.proxy, this.local, addr, abi);
  }

  _rawBalance(
    wallet: AnyCell<Address<Network>>,
    ch: AnyCell<ChainType>,
    tok: AnyCell<TokenQueryOrAddress>
  ) {
    return raw_balance(
      this.core,
      this.proxy,
      this.rpc,
      this.local,
      wallet,
      ch,
      tok
    );
  }

  _getBalance<N extends Network>(
    addr: AnyCell<ChainAddress<N>>,
    owner: AnyCell<Address<N> | null>
  ) {
    return getBalance(this.core, this.proxy, this.rpc, addr, owner);
  }

  // @todo StarkNet
  nativeBalance(
    chain: AnyCell<ChainType>,
    owner: AnyCell<Address<EVMType> | null>
  ) {
    return nativeBalance(this.proxy, this.rpc, chain, owner);
  }

  _getDecimals<N extends Network>(addr: AnyCell<ChainAddress<N>>) {
    return getDecimals(this.local, this.core, this.proxy, this.rpc, addr);
  }

  _getSymbol<N extends Network>(addr: AnyCell<ChainAddress<N>>) {
    return getSymbol(this.local, this.core, this.proxy, this.rpc, addr);
  }

  _getName<N extends Network>(addr: AnyCell<ChainAddress<N>>) {
    return getName(this.local, this.core, this.proxy, this.rpc, addr);
  }

  _getAllowanceForWallet(
    wallet: AnyCell<Address<Network>>,
    addr: AnyCell<ChainAddress>,
    spender: AnyCell<Address<Network>>
  ) {
    return getAllowanceForWallet(this.core, this.rpc, wallet, addr, spender);
  }

  logTitle(wallet: AnyCell<Address<Network>>, aLog: AnyCell<AnalyzedLog>) {
    return logTitle(this.proxy, wallet, aLog);
  }

  logSearch<
    Prop extends string | null,
    Emitter extends AnyAddress | null,
    Filter extends {
      [k: string]: string | Address<Network> | number | bigint;
    } | null
  >(
    chain: AnyCell<ChainType>,
    logs: AnyCell<AnalyzedLog[][]>,
    step: AnyCell<number>,
    event: AnyCell<string>,
    prop: AnyCell<Prop>,
    emitter: AnyCell<Emitter>,
    filter: AnyCell<Filter>
  ) {
    return logSearch(
      this.proxy,
      this.local,
      chain,
      logs,
      step,
      event,
      prop,
      emitter,
      filter
    );
  }

  analyzeLog(log: AnyCell<LogEntry>, ch: AnyCell<ChainType>) {
    return analyzeLog(this, log, ch);
  }

  analyzeLogs(
    chain: AnyCell<ChainType>,
    logsCell: AnyCell<AnyCell<LogEntry>[]>
  ) {
    return analyzeLogs(this, chain, logsCell);
  }

  _retrieveAddress<Source extends AnyAddress>(
    ch: AnyCell<ChainType>,
    addr: AnyCell<Source>
  ) {
    return retrieveAddress(this.proxy, this.local, ch, addr);
  }

  _retrieveAllAddresses(
    ch: AnyCell<ChainType>,
    l: AnyCell<AnyCell<AnyAddress>[]>
  ) {
    return retrieveAllAddresses(this.proxy, this.local, ch, l);
  }

  _generateTX(
    addr: AnyCell<ChainAddress>,
    data: AnyCell<`0x${string}`>,
    value: AnyCell<Rational>,
    estimateGas = true
  ) {
    return generateTX(
      this.rpc,
      this.rpc._proxy,
      this.core,
      addr,
      data,
      value,
      estimateGas
    );
  }

  _estimateGas(ch: AnyCell<ChainType>, tx: AnyCell<TransactionRequest>) {
    return estimateGas(this.proxy, this.rpc, ch, tx);
  }

  _proxyFetch(uri: AnyCell<string>) {
    return proxy_fetch(this.local, uri);
  }

  proxyHead(uri: AnyCell<string>) {
    return proxy_head(this.local, uri);
  }

  _findName(ch: AnyCell<ChainType>, addr: AnyCell<Address<Network>>) {
    return findName(this.rpc, this.proxy, this.core.DefaultContracts, ch, addr);
  }

  dataTypeScheme(
    isAdmin?: boolean,
    mode?: EditorMode,
    type?: DataCacheType,
    methods?: string[]
  ) {
    return coredataTypeScheme(this, isAdmin, mode, type, methods);
  }

  newInteraction(
    types: MapTypeDefinitions,
    contractQuery?:
      | TokenQueryType
      | ContractQueryType
      | AnonContractQueryType<ChainType>,
    method?: string
  ) {
    return newWidget(this.core, this.proxy, types, contractQuery, method);
  }

  newInteractionFrom(types: MapTypeDefinitions, from: OKWidget) {
    return newWidgetFrom(this.core, this.proxy, types, from);
  }

  _callMethod<Args extends AnyCell<unknown>[]>(
    chain: AnyCell<ChainType>,
    q: AnyCell<AnyAddress>,
    meth: AnyCell<string>,
    args: AnyCell<Args>
  ) {
    return callMethod(this.rpc, this.local, chain, q, meth, args);
  }

  _tokenImage(
    chain: AnyCell<ChainType>,
    coll: AnyCell<Address<Network>>,
    id: AnyCell<number>,
    proxy?: string
  ) {
    return token_image(this.rpc, this.local, chain, coll, id, proxy);
  }

  _tokenURI(
    chain: AnyCell<ChainType>,
    coll: AnyCell<Address<Network>>,
    id: AnyCell<number>,
    proxy?: string
  ) {
    return tokenURI_JSON(this.rpc, this.local, chain, coll, id, proxy);
  }

  _ownerOf(
    chain: AnyCell<ChainType>,
    coll: AnyCell<NFTQueryType | Address<Network>>,
    id: number // @todo cell of array?
  ) {
    return owner_of(this.rpc, this.local, chain, coll, id);
  }

  /**
   * stepper instanciates a new Stepper
   */
  stepper(id: string) {
    const widgetCell = this.local.staticQuery(WidgetQuery(id));
    // @todo reactive cellify
    const widgetSteps = widgetCell.map((_widgetCell) =>
      cellify(this.proxy, _widgetCell.st)
    ) as unknown as Cellified<Step<OKWidgetStepType>[]>;
    return new Stepper(this, widgetSteps);
  }

  /**
   * Create new Stepper.
   * @param okc manager
   * @param wid widget ID
   * @param chain wanted chain
   * @returns
   * @todo merge with previous
   */
  _NewStepper(okc: OKContractManager, wid: string) {
    const widCell = this.proxy.new(WidgetQuery(wid), "widCell");
    const widget = this._dataCache(widCell, {
      name: "widget",
      returnErrors: true
    });
    const steps = this.proxy.map([widget], (w) =>
      cellify(this.proxy, w.st)
    ) as unknown as Cellified<Step<OKWidgetStepType>[]>;
    return new Stepper(this, steps);
  }

  fork(name: string): OKPage {
    return new OKPage(this.core, {
      ...this._options,
      wantedChain: this.wantedChain,
      name
    });
  }
}
